import { useEffect, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { Button } from 'primereact/button';
import { Menu } from 'primereact/menu';
import { EvaluateTable, DetailInfo } from 'features/AcceptanceReview/components';
import {
  downloadDocxFile,
  downloadDocxTemplate,
  downloadPdfFile,
  downloadPdfTemplate,
  getReviewerInfo,
  getDetail,
} from 'apis/acceptanceReviews.api';

import Loading from 'components/Loading';
import { TOAST } from 'constant';
import { toast } from 'layout';

import { getAcadamicLevelShortHand, is1593, toNonAccentVietnamese } from 'utils/func';
import RichTextViewer from 'components/RichTextViewer';
import DynamicAcceptanceForm from 'features/AcceptanceReview/components/DynamicAcceptanceReview';

export default function AcceptanceReviewDetail() {
  // #region Data
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { projectProposalId } = useParams();
  const [searchParams] = useSearchParams();
  const councilMemberId = searchParams.get('councilMemberId');
  const userId = searchParams.get('userId');
  const councilId = searchParams.get('councilId');
  const downloadButtonRef = useRef();
  const downloadTemplateButtonRef = useRef();

  // A. General Information
  const { data: infoData, isLoading } = useQuery(
    ['acceptance-reviewer', councilId, projectProposalId],
    () => getReviewerInfo(userId, projectProposalId, councilId, projectProposalId)
  );
  const info = useMemo(() => infoData?.data, [infoData]);

  const template = useMemo(
    () => info?.projectProposal?.template,
    [info]
  );

  const { data: acceptanceReviewData, isLoading: isLoadingAcceptanceReview } = useQuery(
    ['acceptance-review', councilId, projectProposalId],
    () => getDetail(councilMemberId, projectProposalId, councilId),
    {
      enabled: !isLoading && !is1593(template),
    }
  );
  const acceptanceReview = useMemo(() => acceptanceReviewData?.data, [acceptanceReviewData]);

  const {
    control,
    watch,
    setValue,
    getValues,
    reset,
    formState: { errors },
  } = useForm({ mode: 'onChange' });

  // Fill available data to form
  useEffect(() => {
    if (acceptanceReview?.acceptanceReview) {
      const { acceptanceReview: review } = acceptanceReview;
      const {
        scoreTarget,
        scoreContent,
        scoreApproach,
        scoreProcess,
        scoreApplicationProduct,
        scorePublishedProduct,
        scoreTrainingProduct,
        scoreReportQuality,
      } = review;
      const scoreResponse = parseInt(scoreTarget + scoreContent + scoreApproach + scoreProcess, 10);
      const scoreQuantityAndQuality = parseInt(
        scoreApplicationProduct + scorePublishedProduct + scoreTrainingProduct,
        10
      );
      setValue('score.scoreApproach', review.scoreApproach);
      setValue('score.scoreTarget', scoreTarget);
      setValue('score.scoreContent', scoreContent);
      setValue('score.scoreProcess', scoreProcess);
      setValue('score.scoreResponse', scoreResponse);
      setValue('score.scoreApplicationProduct', scoreApplicationProduct);
      setValue('score.scorePublishedProduct', scorePublishedProduct);
      setValue('score.scoreTrainingProduct', scoreTrainingProduct);
      setValue('score.scoreQuantityAndQuality', scoreQuantityAndQuality);
      setValue('score.scoreReportQuality', scoreReportQuality);

      reset({}, { keepValues: true });
    }
  }, [acceptanceReview, acceptanceReview?.acceptanceReview, setValue]);

  // #endregion

  // #region Events

  const {
    refetch: handleDownloadPdfFile,
    isLoading: isDownloadPdfFileLoading,
    isFetching: isDownloadPdfFileFetching,
  } = useQuery(
    ['downloadPdfFileAcceptanceReview', projectProposalId, councilId, councilMemberId],
    () =>
      downloadPdfFile({
        projectProposalId,
        councilId,
        councilMemberId,
        referenceId: projectProposalId,
      }),
    {
      enabled: false,
      onSuccess: (res) => {
        if (res?.data) {
          const file = new Blob([res.data]);

          const academicLevelShorthand = getAcadamicLevelShortHand(
            info?.user?.scientificProfile?.academicRank,
            info?.user?.scientificProfile?.academicDegree,
            t,
            true
          );
          const filename = `Phieu nhan xet danh gia nghiem thu - ${academicLevelShorthand}${toNonAccentVietnamese(
            info?.user?.fullname
          )}.pdf`;

          const link = document.createElement('a');
          link.href = URL.createObjectURL(file);
          link.download = filename;
          link.click();
        }
      },
      onError: () => {
        toast(TOAST.ERROR, t('projectProposal.review.exportPdfErrorTitle'));
      },
    }
  );

  const {
    refetch: handleDownloadDocxFile,
    isLoading: isDownloadDocxFileLoading,
    isFetching: isDownloadDocxFileFetching,
  } = useQuery(
    ['downloadDocxFileAcceptanceReview', projectProposalId, councilId, councilMemberId],
    () =>
      downloadDocxFile({
        projectProposalId,
        councilId,
        councilMemberId,
        referenceId: projectProposalId,
      }),
    {
      enabled: false,
      onSuccess: (res) => {
        if (res?.data) {
          const file = new Blob([res.data]);

          const academicLevelShorthand = getAcadamicLevelShortHand(
            info?.user?.scientificProfile?.academicRank,
            info?.user?.scientificProfile?.academicDegree,
            t,
            true
          );
          const filename = `Phieu nhan xet danh gia nghiem thu - ${academicLevelShorthand}${toNonAccentVietnamese(
            info?.user?.fullname
          )}.docx`;

          const link = document.createElement('a');
          link.href = URL.createObjectURL(file);
          link.download = filename;
          link.click();
        }
      },
      onError: () => {
        toast(TOAST.ERROR, t('projectProposal.review.exportDocxErrorTitle'));
      },
    }
  );

  const {
    refetch: handleDownloadPdfTemplate,
    isLoading: isDownloadPdfTemplateLoading,
    isFetching: isDownloadPdfTemplateFetching,
  } = useQuery(
    ['downloadPdfTemplateAcceptanceReview', projectProposalId, councilId, councilMemberId],
    () =>
      downloadPdfTemplate({
        projectProposalId,
        councilId,
        councilMemberId,
        referenceId: projectProposalId,
      }),
    {
      enabled: false,
      onSuccess: (res) => {
        if (res?.data) {
          const file = new Blob([res.data]);

          const filename = 'Mau M08 - Phieu nhan xet danh gia nghiem thu.pdf';

          const link = document.createElement('a');
          link.href = URL.createObjectURL(file);
          link.download = filename;
          link.click();
        }
      },
      onError: () => {
        toast(TOAST.ERROR, t('projectProposal.review.exportPdfErrorTitle'));
      },
    }
  );

  const {
    refetch: handleDownloadDocxTemplate,
    isLoading: isDownloadDocxTemplateLoading,
    isFetching: isDownloadDocxTemplateFetching,
  } = useQuery(
    ['downloadDocxTemplateAcceptanceReview', projectProposalId, councilId, councilMemberId],
    () =>
      downloadDocxTemplate({
        projectProposalId,
        councilId,
        councilMemberId,
        referenceId: projectProposalId,
      }),
    {
      enabled: false,
      onSuccess: (res) => {
        if (res?.data) {
          const file = new Blob([res.data]);

          const filename = 'Mau M08 - Phieu nhan xet danh gia nghiem thu.docx';

          const link = document.createElement('a');
          link.href = URL.createObjectURL(file);
          link.download = filename;
          link.click();
        }
      },
      onError: () => {
        toast(TOAST.ERROR, t('projectProposal.review.exportDocxErrorTitle'));
      },
    }
  );

  const renderFirstTab = () => {
    if (is1593(template) || isLoading) {
      return (
        <DynamicAcceptanceForm
          projectProposalId={projectProposalId}
          councilId={councilId}
          councilMemberId={councilMemberId}
          generalInfo={info?.projectProposal}
          isLoading={isLoading}
          readOnly
        />
      );
    }
    return (
      <div className="m-0 bg-white px-4 sm:px-4 md:px-4 lg:px-6 xl:px-8 mb-3 py-5 p-fluid relative financial-review-edit">
        <div className="flex gap-1 justify-content-end">
          {acceptanceReview?.id && (
            <Button
              label={t('council.review.exportFile')}
              icon={`pi ${
                isDownloadPdfFileLoading ||
                isDownloadPdfFileFetching ||
                isDownloadDocxFileLoading ||
                isDownloadDocxFileFetching
                  ? 'pi-spin pi-spinner'
                  : 'pi-file'
              }`}
              className="block w-auto h-3rem border-none"
              severity="info"
              onClick={(event) => downloadButtonRef.current.toggle(event)}
              disabled={
                isDownloadPdfFileLoading ||
                isDownloadPdfFileFetching ||
                isDownloadDocxFileLoading ||
                isDownloadDocxFileFetching
              }
            />
          )}

          <Button
            // type="button"
            label={t('council.review.downloadTemplate')}
            icon={`pi ${
              isDownloadPdfTemplateLoading ||
              isDownloadPdfTemplateFetching ||
              isDownloadDocxTemplateLoading ||
              isDownloadDocxTemplateFetching
                ? 'pi-spin pi-spinner'
                : 'pi-file'
            }`}
            className="block w-auto h-3rem border-none"
            severity="info"
            onClick={(event) => downloadTemplateButtonRef.current.toggle(event)}
            disabled={
              isDownloadPdfTemplateLoading ||
              isDownloadPdfTemplateFetching ||
              isDownloadDocxTemplateLoading ||
              isDownloadDocxTemplateFetching
            }
          />
        </div>
        <Menu
          ref={downloadButtonRef}
          model={[
            {
              label: t('council.review.exportPdf'),
              icon: 'pi pi-file-pdf',
              command: handleDownloadPdfFile,
              disabled: isDownloadPdfFileLoading || isDownloadPdfFileFetching,
            },
            {
              label: t('council.review.exportDocx'),
              icon: 'pi pi-file-word',
              command: handleDownloadDocxFile,
              disabled: isDownloadDocxFileLoading || isDownloadDocxFileFetching,
            },
          ]}
          popup
        />
        <Menu
          ref={downloadTemplateButtonRef}
          model={[
            {
              label: t('council.review.downloadPdfTemplate'),
              icon: 'pi pi-file-pdf',
              command: handleDownloadPdfTemplate,
              disabled: isDownloadPdfTemplateLoading || isDownloadPdfTemplateFetching,
            },
            {
              label: t('council.review.downloadDocxTemplate'),
              icon: 'pi pi-file-word',
              command: handleDownloadDocxTemplate,
              disabled: isDownloadDocxTemplateLoading || isDownloadDocxTemplateFetching,
            },
          ]}
          popup
        />

        <div className="mb-5 flex justify-content-between align-items-center">
          <img
            src={`${process.env.PUBLIC_URL}/logo.png`}
            alt="VNU-HCM logo"
            className="ml-3 h-4rem md:h-5rem"
          />
          <div className="pl-2">
            {t('projectProposal.viewDetail.form')} M08 <br />
            {t('council.m08.projectCode')}
          </div>
        </div>
        <div className="text-center mb-6">
          <h3 className="font-bold mb-0">{t('council.m08.title')}</h3>
          <h3 className="mt-0">{t('council.m08.sciTechProject')}</h3>
        </div>

        <DetailInfo info={info} />

        {/* Section A */}
        <div className="mt-5">
          <h4>{t('council.m08.a.label')}</h4>

          {/* A1 */}
          <div className="text-lg mt-3">
            <h5>{t('council.m08.a.a1')}</h5>
            <p className="font-italic">{t('council.m08.a.a1_note')}</p>
            <RichTextViewer
              style={{ height: '100%', width: '100%' }}
              value={acceptanceReview?.acceptanceReview?.reviewMeet}
              showHeader={false}
              readOnly
            />
          </div>

          {/* A2 */}
          <div className="text-lg mt-3">
            <h5 className="mb-2">{t('council.m08.a.a2')}</h5>
            <RichTextViewer
              style={{ height: '100%', width: '100%' }}
              value={acceptanceReview?.acceptanceReview?.reviewProduct}
              showHeader={false}
              readOnly
            />
          </div>

          {/* A3 */}
          <div className="text-lg mt-3">
            <h5>{t('council.m08.a.a3')}</h5>
            <p className="font-italic">{t('council.m08.a.a3_note')}</p>
            <RichTextViewer
              style={{ height: '100%', width: '100%' }}
              value={acceptanceReview?.acceptanceReview?.reviewReportQuality}
              showHeader={false}
              readOnly
            />
          </div>
        </div>

        <form autoComplete="off">
          <EvaluateTable
            setValue={setValue}
            getValues={getValues}
            watch={watch}
            control={control}
            errors={errors}
            researchType={info?.projectProposal?.researchType}
            readOnly
          />

          <h5 className="font-bold">{t('council.m08.b.rank.label')}</h5>
          <ul>
            <li>{t('council.m08.b.rank.excellent')}</li>
            <li>{t('council.m08.b.rank.veryGood')}</li>
            <li>{t('council.m08.b.rank.good')}</li>
            <li>{t('council.m08.b.rank.reached')}</li>
            <li>{t('council.m08.b.rank.notReached')}</li>
          </ul>
          <div className="text-lg mt-3">
            <h5>{t('council.m08.c.conclusion')}</h5>
            <RichTextViewer
              style={{ height: '100%', width: '100%' }}
              value={acceptanceReview?.acceptanceReview?.reviewConclusion}
              showHeader={false}
              readOnly
            />
          </div>
          <div className="grid text-lg mt-3">
            <div className="col-7 px-8">
              <span>{t('council.m01.c.commitment')}</span>
            </div>
            <div className="col-5 text-center">
              <p className="mb-0">{t('signature.date')}</p>
              <p className="font-semibold mb-0">{t('signature.reviewer')}</p>
              <p className="mb-7">{t('signature.fullnameAndSignature')}</p>
              <p className="font-bold">{info?.user?.fullname}</p>
            </div>
          </div>

          <div className="flex justify-content-end mt-4">
            <Button
              label={t('formLayout.action.close')}
              severity="danger"
              type="button"
              className="w-8rem"
              onClick={() => navigate(-1)}
            />
          </div>
        </form>
      </div>
    );
  };
  // #endregion

  return (
    <>
      {isLoadingAcceptanceReview && <Loading />} {renderFirstTab()}
    </>
  );
}
